import { ref, computed, watch } from 'vue';
import store from '@/store';
import { config } from '@/config.js';
import { useQueryList } from '@/composables/common/useQueryList';
import { useListaSelection } from '@/composables/admin/bases/useListaSelection';

export function useAbonados() {
    // ==================== LISTA ACTIVA (URL-driven) ====================
    const { listaId, listaActiva: lista_abonados } = useListaSelection();

    // ==================== LIST QUERY (useQueryList) ====================
    const list = useQueryList({
        fetcher: async params => {
            // Si no hay lista activa, devolvemos vacío
            if (!listaId.value) {
                return { data: [], meta: {} };
            }

            // Aplanar search_for + search en un único parámetro:
            // search_for='n_abonado' + search='5' → { n_abonado: '5' }
            // El backend espera el filtro nombrado por el campo (n_abonado, dni,
            // nombre_apellidos…), no los dos genéricos.
            const flatFilters = { ...params.filters };
            if (flatFilters.search_for && flatFilters.search) {
                const key = flatFilters.search_for;
                flatFilters[key] = flatFilters.search;
                delete flatFilters.search;
                delete flatFilters.search_for;
            }

            const res = await store.dispatch(
                config.getVuexAlias('back.abonados.index'),
                {
                    filters: {
                        ...flatFilters,
                        lista_abonado_id: listaId.value,
                    },
                    lista_id: params.lista_id,
                }
            );

            if (res.data?.data) {
                res.data.data = res.data.data.filter(ab => ab.n_abonado !== -10);
            }

            return res.data || { data: [], meta: {} };
        },
        extraFilters: ['search_for', 'tipo', 'metodo_pago', 'tipo_abono', 'tipo_precio', 'activos'],
        defaultFilterValues: {
            search_for: 'n_abonado',
            tipo: '0',
            metodo_pago: '',
            tipo_abono: null,
            tipo_precio: null,
            activos: '',
        },
        watchListaId: true,
    });

    const {
        filters,
        data: abonados,
        loading,
        meta,
        fetch: getAbonados,
        updateUrlDebounced,
    } = list;

    // ==================== RESET FILTERS (custom: distintos a defaultFilters) ====================
    function resetFilters() {
        Object.assign(filters.value, {
            search: '',
            search_for: 'nombre_apellidos',
            tipo: '',
            metodo_pago: '',
            tipo_abono: null,
            tipo_precio: null,
            activos: '',
            page: 1,
            per_page: 10,
        })
        getAbonados()
    }

    // ==================== SECUNDARY DATA ====================
    const loadingTipos = ref(false);
    const loadingMetodos = ref(false);

    const tipos_abono = ref([]);
    const metodos_pago = ref([]);

    async function getTiposAbono() {
        if (!listaId.value) return

        loadingTipos.value = true

        const params = { filters: { lista_abonado_id: listaId.value } }

        try {
            const res = await store.dispatch(config.getVuexAlias('back.abonados.tipos.abonos'), params)
            tipos_abono.value = res.data.tipos_abonado || []
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error fetching tipos_abono:', error)
            }
        } finally {
            loadingTipos.value = false
        }
    }

    async function getMetodosPago() {
        if (!listaId.value) return

        loadingMetodos.value = true

        const params = { filters: { lista_abonado_id: listaId.value } }

        try {
            const res = await store.dispatch(config.getVuexAlias('back.abonados.metodos-pago'), params)
            metodos_pago.value = res.data.metodos_pago || []
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error fetching metodos_pago:', error)
            }
        } finally {
            loadingMetodos.value = false
        }
    }

    // ==================== COMPUTED ====================
    const teatro = computed(() => store.getters.getTeatro)
    const configuracion = computed(() => store.getters.getConfig)
    const permisos = computed(() => store.getters.getPermisos)
    const manager = computed(() => store.getters.getFooter)

    const columns = [
        { key: 'renovado', label: 'Estado', field: 'esta_renovado', centered: true, class: 'tw:w-20' },
        { key: 'numero', label: 'Nº de abonado', field: 'n_abonado', centered: true, class: 'tw:w-28' },
        { key: 'origen', label: 'Origen', field: 'origen' },
        { key: 'dni', label: 'DNI', field: 'dni', class: 'tw:w-32' },
        { key: 'nombre', label: 'Nombre', field: 'nombreapellidos', class: 'tw:w-64' },
        { key: 'email', label: 'Email', field: 'email', class: 'tw:w-64' },
        { key: 'acciones', label: 'Acciones', centered: true, class: 'tw:w-32' },
    ]

    const disabled = computed(() => loading.value || loadingTipos.value || loadingMetodos.value)

    const url_imprimir_abonos = computed(() => {
        if (!abonados.value?.data?.length) return ''
        const abonadosIds = abonados.value.data.map(ab => ab.id).join(',')
        const query = new URLSearchParams(abonadosIds ? { abonados: abonadosIds } : {}).toString()
        return config.API_BASE
            + '/abonados/abonos/imprimir-busqueda?api_key='
            + localStorage.getItem('token')
            + '&' + query
            + '&tid=' + (configuracion.value?.hash_teatro || '')
    })

    // ==================== WATCH: lista_id changes ====================
    // Re-fetch secondary data on initial mount AND when lista_id changes.
    // useQueryList already handles the main list refetch via watchListaId: true.
    watch(listaId, (newId, oldId) => {
        if (newId && newId !== oldId) {
            getTiposAbono()
            getMetodosPago()
        }
    }, { immediate: true })

    // ==================== PUBLIC API ====================
    return {
        // List query (from useQueryList)
        filters,
        abonados,
        loading,
        meta,
        getAbonados,
        updateUrlDebounced,
        resetFilters,

        // Secundary data
        loadingTipos,
        loadingMetodos,
        tipos_abono,
        metodos_pago,
        lista_abonados,

        // Computed
        teatro,
        configuracion,
        permisos,
        manager,
        disabled,
        columns,
        url_imprimir_abonos,
    }
}